import { GetStaticPaths, GetStaticPropsContext } from "next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";

import { useUser } from "@shared/auth";
import {
  AdCard,
  GenericAd,
  Ratio,
  SidebarOverlay,
  QuickInfo,
} from "@shared/components";
import { urls } from "@shared/constants";
import { Text, Container, Grid, Button } from "@shared/elements";
import { useAd, graphqlFetch, useGraphSWR } from "@shared/hooks";
import { VideoFile } from "@shared/types";

import {
  ArchivePageIndexQuery,
  ArchivePageCallQuery,
  type TArchivePageIndexQuery,
  type TArchivePageCallQuery,
} from "__generated__/types";

import AppHeader from "src/components/AppHeader";
import CallCard from "src/components/CallCard";
import CallGridItem from "src/components/CallGridItem";
import DefaultOpenGraphTags from "src/components/DefaultOpenGraphTags";
import Footer from "src/components/Footer";
import NewsletterForm from "src/components/NewsletterForm";
import Pagination from "src/components/Pagination";
import PublicHeader from "src/components/PublicHeader";

import * as constants from "src/constants";
import { useCallCardLink, useQuickInfoClose } from "src/helpers";
import bundleMessages from "src/utils/bundleMessages";

export default function Index({ archive }: TArchivePageIndexQuery) {
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.archive`);
  const gridAds = [useAd("Contest Grid Card"), useAd("Contest Grid Card", 1)];
  const { ad: bannerAd } = useAd("Contest Grid Banner");
  const router = useRouter();
  const callCardLink = useCallCardLink();

  const { isAuthenticated } = useUser({
    redirectTo: undefined,
    returnAfterAuthentication: true,
  });

  const callId = router.query.quickInfo as string | undefined;
  const { data: callData } = useGraphSWR<TArchivePageCallQuery>(
    ArchivePageCallQuery,
    {
      id: callId,
    },
  );

  const quickInfoCall = callData?.call;

  const onQuickInfoClose = useQuickInfoClose(quickInfoCall?.title?.en);

  const renderCallCard = (
    call: React.ComponentProps<typeof CallGridItem>["call"],
  ) => <CallGridItem key={call.id} call={call} resolveLink={callCardLink} />;

  return (
    <>
      <Head>
        <title key="title">Picter Contests</title>
        <DefaultOpenGraphTags />
      </Head>
      {!isAuthenticated ? (
        <>
          <PublicHeader />
          <Container className="bg-gradient-to-r from-primary-300 to-blue-200 text-secondary-900 pt-24 pb-14 lg:overflow-hidden">
            <div className="lg:flex lg:items-center">
              <div className="md:pt-24 lg:pb-24">
                <Text
                  as="h1"
                  size="6xl"
                  color="current"
                  weight="bold"
                  className="mt-4 sm:mt-5 lg:mt-6 leading-10 sm:leading-none"
                >
                  {t("hero.header")}
                </Text>
                <Text
                  as="p"
                  size="lg"
                  color="current"
                  className="mt-3 sm:mt-5 sm:text-xl"
                >
                  {t.rich("hero.subline", {
                    link: (string) => (
                      <Text
                        as={Link}
                        size="lg"
                        color="primary"
                        // @ts-expect-error TODO: fix typing in Text component to allow extending props depending on the as prop
                        href={constants.paths.index}
                        className="sm:text-xl"
                      >
                        {string}
                      </Text>
                    ),
                  })}
                </Text>
              </div>
            </div>
          </Container>
          {/* end of !isAuthenticated */}
        </>
      ) : (
        <>
          <AppHeader />
          <Container className="mt-12">
            {bannerAd && (
              <GenericAd
                // We need to change the id on each archive page to track impressions
                // for each page individually. Otherwise, the impression tracking will
                // stop working after the first page (since the ids are the same).
                key={`${bannerAd.id}-${archive.page}`}
                campaign={bannerAd.campaign}
                type={bannerAd.content.mediaType}
                src={bannerAd.content.files.map(({ src }) => src)}
                impressionUrl={bannerAd.impressionUrl}
                trackingTags={bannerAd.trackingTags}
                clickUrl={bannerAd.clickUrl}
                poster={
                  bannerAd.content.mediaType === "video"
                    ? bannerAd.content.files.map(
                        (adFile) => (adFile as VideoFile).poster,
                      )
                    : undefined
                }
                ratio={[Ratio.Square, Ratio.FourByOne]}
              />
            )}
          </Container>
        </>
      )}
      <Container className="mt-14">
        <Text as="h2" weight="bold" size="3xl">
          {t("listHeader")}
        </Text>
        <Grid className="mt-12 sm:gap-y-14">
          {archive.calls.slice(0, 1).map(renderCallCard)}
          {gridAds[0]?.ad && (
            <Grid.Item key={gridAds[0]?.ad.id}>
              <AdCard
                key={`${gridAds[0]?.ad.id}-${archive.page}`}
                title={gridAds[0]?.ad.content.headline}
                campaign={gridAds[0]?.ad.campaign}
                type={gridAds[0]?.ad.content.mediaType}
                src={gridAds[0]?.ad.content.files[0].src}
                impressionUrl={gridAds[0]?.ad.impressionUrl}
                trackingTags={gridAds[0]?.ad.trackingTags}
                clickUrl={gridAds[0]?.ad.clickUrl}
                poster={
                  gridAds[0]?.ad.content.mediaType === "video"
                    ? (gridAds[0]?.ad.content.files[0] as VideoFile).poster
                    : undefined
                }
              />
            </Grid.Item>
          )}
          {archive.calls.slice(1, 6).map(renderCallCard)}
          {gridAds[1]?.ad && (
            <Grid.Item key={gridAds[1]?.ad.id}>
              <AdCard
                key={`${gridAds[1]?.ad.id}-${archive.page}`}
                title={gridAds[1]?.ad.content.headline}
                campaign={gridAds[1]?.ad.campaign}
                type={gridAds[1]?.ad.content.mediaType}
                src={gridAds[1]?.ad.content.files[0].src}
                impressionUrl={gridAds[1]?.ad.impressionUrl}
                trackingTags={gridAds[1]?.ad.trackingTags}
                clickUrl={gridAds[1]?.ad.clickUrl}
                poster={
                  gridAds[1]?.ad.content.mediaType === "video"
                    ? (gridAds[1]?.ad.content.files[0] as VideoFile).poster
                    : undefined
                }
              />
            </Grid.Item>
          )}
          {archive.calls.slice(6).map(renderCallCard)}
        </Grid>
      </Container>
      <Container className="mt-12">
        <Pagination
          getPageLink={urls.archive}
          page={archive.page}
          pages={archive.pages}
        />
      </Container>
      {!isAuthenticated ? (
        <Container className="mt-12">
          {bannerAd && (
            <GenericAd
              key={`${bannerAd.id}-${archive.page}`}
              campaign={bannerAd.campaign}
              type={bannerAd.content.mediaType}
              src={bannerAd.content.files.map(({ src }) => src)}
              impressionUrl={bannerAd.impressionUrl}
              trackingTags={bannerAd.trackingTags}
              clickUrl={bannerAd.clickUrl}
              poster={
                bannerAd.content.mediaType === "video"
                  ? bannerAd.content.files.map(
                      (adFile) => (adFile as VideoFile).poster,
                    )
                  : undefined
              }
              ratio={[Ratio.Square, Ratio.FourByOne]}
            />
          )}
        </Container>
      ) : null}
      <Container className="mt-12 bg-gradient-to-r from-primary-300 to-blue-200 text-secondary-900 pt-24 lg:pb-14 lg:overflow-hidden">
        <div className="lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="mx-auto max-w-md sm:max-w-3xl lg:flex lg:items-center">
            <div className="md:pt-24 lg:pb-24">
              <Text
                as="h1"
                size="6xl"
                color="current"
                weight="bold"
                className="mt-4 sm:mt-5 lg:mt-6 leading-10 sm:leading-none"
              >
                {t("bottomHero.header")}
              </Text>
              <Text
                as="p"
                size="lg"
                color="current"
                className="mt-3 sm:mt-5 sm:text-xl"
              >
                {t("bottomHero.subline")}
              </Text>
              <div className="mt-10 sm:mt-12">
                <Button
                  variant="primary"
                  size="lg"
                  as={Link}
                  href={constants.paths.index}
                >
                  {t("bottomHero.cta")}
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-12 lg:m-0 lg:relative">
            <div className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:max-w-none lg:px-0">
              <Image
                className="w-full lg:absolute lg:inset-y-0 lg:left-0 lg:h-full lg:w-auto lg:max-w-none"
                src="/assets/logo-grid-perspective.svg"
                width={1000}
                height={1000}
                alt="Brands using Picter"
              />
            </div>
          </div>
        </div>
      </Container>
      <Footer />
      <SidebarOverlay
        show={Boolean(quickInfoCall)}
        size="wide"
        onClose={onQuickInfoClose}
      >
        {quickInfoCall && (
          <QuickInfo
            data={quickInfoCall}
            showFooterLogo={false}
            utmMedium="call_grid"
          />
        )}
      </SidebarOverlay>
    </>
  );
}

export const getStaticPaths: GetStaticPaths<{ id: string }> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export async function getStaticProps({
  locale,
  params,
}: GetStaticPropsContext) {
  const { archive }: TArchivePageIndexQuery = await graphqlFetch(
    JSON.stringify({
      query: ArchivePageIndexQuery,
      variables: { limit: 22, page: parseInt(params.page as string, 10) },
    }),
  );
  const messages = await bundleMessages(locale, [
    `${constants.MESSAGE_PREFIX}pages.archive`,
    ...AdCard.messages,
    ...CallCard.messages,
    ...Footer.messages,
    ...GenericAd.messages,
    ...NewsletterForm.messages,
    ...PublicHeader.messages,
    ...AppHeader.messages,
    ...SidebarOverlay.messages,
    ...QuickInfo.messages,
    ...Pagination.messages,
  ]);
  return {
    props: {
      archive,
      messages,
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 1 hour
    revalidate: 60 * 60, // In seconds
  };
}
